import accepts from 'attr-accept';
import clsx from 'clsx';
import { isArray } from 'lodash';
import { nanoid } from 'nanoid';
import React, { FC, useState, ReactNode } from 'react';
import tinyColor from 'tinycolor2';

import UploadedDocumentStub from 'components/common/UploadedDocumentStub';
import { DocumentExtension } from 'product_modules/api/LoanOriginationSystem/DocumentsApi';
import Label from 'product_modules/components/Label';
import { PptIcon, XlsIcon, DocIcon, JsonIcon, ImageIcon, PdfIcon } from 'product_modules/static/images';

import styles from './UploadFile.module.scss';

export const iconsByExtension = new Map([
  [DocumentExtension.Xls, <XlsIcon />],
  [DocumentExtension.Xlsx, <XlsIcon />],
  [DocumentExtension.Csv, <XlsIcon />],
  [DocumentExtension.Doc, <DocIcon />],
  [DocumentExtension.Docx, <DocIcon />],
  [DocumentExtension.Xml, <DocIcon />],
  [DocumentExtension.Txt, <DocIcon />],
  [DocumentExtension.Ppt, <PptIcon />],
  [DocumentExtension.Pptx, <PptIcon />],
  [DocumentExtension.Pdf, <PdfIcon />],
  [DocumentExtension.Json, <JsonIcon />],
  [DocumentExtension.Gif, <ImageIcon />],
  [DocumentExtension.Png, <ImageIcon />],
  [DocumentExtension.Jpeg, <ImageIcon />],
  [DocumentExtension.Jpg, <ImageIcon />],
  [DocumentExtension.Svg, <ImageIcon />],
  [DocumentExtension.Ico, <ImageIcon />],
  [DocumentExtension.Heic, <ImageIcon />],
  [DocumentExtension.Webp, <ImageIcon />],
]);

const INCORRECT_FILE_FORMAT_ERROR_MESSAGE = 'File format is not supported';
const FILE_SIZE_EXCEEDED_ERROR_MESSAGE = 'Maximum file size exceeded';

export interface IUploadFromFileProps {
  file?: File | null;
  files?: File[] | null;
  onFileChange: (file: File | null, index?: number) => void;
  accept?: string | string[];
  maxFileSize?: number;
  text?: ReactNode;
  closeIcon?: React.ReactNode;
  brandingColor?: string;
  inputLabel?: string;
  browseLinkClassName?: string;
  className?: string;
  fileDeleteContainerClassName?: string;
  dropDisabled?: boolean;
  required?: boolean;
}

const validateFileSize = (fileSize: number, maxFileSize?: number) => {
  if (typeof maxFileSize === 'undefined') {
    return true;
  }

  return fileSize <= maxFileSize;
};

const BROWSE_LINK_DARKNESS_PERCENTAGE = 5;

const INPUT_ID = 'fileInput';

const UploadFile: FC<IUploadFromFileProps> = ({
  file,
  files,
  onFileChange,
  accept,
  maxFileSize,
  text,
  closeIcon,
  brandingColor,
  inputLabel,
  browseLinkClassName,
  className,
  dropDisabled,
  required,
  fileDeleteContainerClassName,
}) => {
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isBrowseLinkHovered, setIsBrowseLinkHovered] = useState(false);

  const validateFile = (newFile: File, index?: number) => {
    if (!newFile) return;

    const isAcceptable = !accept || accepts(newFile, accept);

    if (!isAcceptable) {
      setErrorMessage(INCORRECT_FILE_FORMAT_ERROR_MESSAGE);
      return;
    }

    if (!validateFileSize(newFile.size, maxFileSize)) {
      setErrorMessage(FILE_SIZE_EXCEEDED_ERROR_MESSAGE);
      return;
    }

    setErrorMessage(null);
    onFileChange(newFile, index);
  };

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>, index?: number) => {
    const newFile = event.target!.files![0];
    validateFile(newFile, index);
  };

  const handleFileDrag = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const newFile = e.dataTransfer.files[0];
    validateFile(newFile);
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.stopPropagation();
    e.preventDefault();

    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.stopPropagation();
    e.preventDefault();

    setIsDragOver(false);
  };

  const labelClassName = clsx(
    isDragOver ? styles.dragOverInputWrapper : styles.inputWrapper,
    errorMessage && styles.errorInputWrapper,
  );

  const renderBrowserLink = () => {
    const browseLinkStyle = {
      color: isBrowseLinkHovered
        ? tinyColor(brandingColor).darken(BROWSE_LINK_DARKNESS_PERCENTAGE).toString()
        : brandingColor,
    };

    return (
      <span
        style={browseLinkStyle}
        onMouseEnter={() => setIsBrowseLinkHovered(true)}
        onMouseLeave={() => setIsBrowseLinkHovered(false)}
        className={clsx(styles.link, browseLinkClassName, dropDisabled && styles.browseLinkDisabled)}
      >
        browse
      </span>
    );
  };

  const renderUploadedFileInfo = (uploadedFile: File, index?: number) => (
    <UploadedDocumentStub
      key={nanoid()}
      fileName={uploadedFile.name}
      onFileChange={onFileChange}
      closeIcon={closeIcon}
      index={index}
      fileDeleteContainerClassName={fileDeleteContainerClassName}
    />
  );

  const renderDropZone = () => (
    <>
      <label
        className={clsx(labelClassName, dropDisabled && styles.disabled)}
        onDrop={handleFileDrag}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <p className={styles.textContainer}>
          {text || (
            <>
              Drag a file here or {renderBrowserLink()} for a file to upload.
            </>
          )}
        </p>
        <input
          type="file"
          value=""
          id={INPUT_ID}
          className={styles.fileInput}
          onChange={handleUploadFile}
          accept={isArray(accept) ? accept.join(',') : accept}
        />
      </label>
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
    </>
  );

  return (
    <div className={className}>
      {inputLabel ? (
        <Label htmlFor={INPUT_ID} required={required}>
          {inputLabel}
        </Label>
      ) : null}
      <div className={styles.dropZoneContainer}>
        {file ? renderUploadedFileInfo(file) : renderDropZone()}
      </div>
      <div className={styles.multipleFilesContainer}>
        {files?.map((uploadedFile, index) => renderUploadedFileInfo(uploadedFile, index))}
      </div>
    </div>
  );
};

export default UploadFile;
